import { MotionDiv } from '@/components/element/MotionDiv';
import { useUpdateSalesOrderContractBillingScheduleChecked } from '@/hooks/api/1661_update_billing_schedule_checked';
import { useUpdateNoneDocuments } from '@/hooks/api/1662_update_none_documents';
import { useUpdateSalesOrderContractLegalChecked } from '@/hooks/api/1663_update_legal_checked';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentSalesOrderContractTask } from '@/providers/SalesOrderContractTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuotationBoardAmount } from '@/hooks/api/2520_quotation_board_amount';

export const ToDoList = () => {
  const { meTeamMember } = useCurrentTeamMember();
  const { salesOrderContractTask: task, setSalesOrderContractTask } = useCurrentSalesOrderContractTask();
  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    if (task) {
      setDisabled(task!.sales_order_contract_task_phase.name != 'preparing');
    }
  }, [task]);

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header">
        <h6 className="mb-1">TODOリスト</h6>
        <p className="text-xs mb-0">TODOを処理して、タスクを次のフェーズへ移動させてください。</p>
      </div>
      <div className="card-body pt-0">
        <ul className="list-group list-group-flush" data-toggle="checklist">
          <UpdateNoneDocumentElement disabled={disabled} />
          {task!.contract_context.name != 'nda' && <BillingScheduleCheckElement disabled={disabled} />}
          <LegalCheckElement disabled={disabled} />
        </ul>
      </div>
    </MotionDiv>
  );
};

const UpdateNoneDocumentElement = ({ disabled }: { disabled: boolean }) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember, me } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { salesOrderContractTask: task, setSalesOrderContractTask } = useCurrentSalesOrderContractTask();
  const { mutate, data, isPending, error } = useUpdateNoneDocuments();

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onNoneDocumentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id!),
      id: task!.id,
      none_documents: e.target.checked,
    });
  };

  useEffect(() => {
    if (data) setSalesOrderContractTask((_) => data);
  }, [data]);

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 mb-3">
      <div
        className={`checklist-item ps-2 ms-3 ${
          task!.has_document || task!.none_documents ? 'checklist-item-success' : 'checklist-item-danger'
        }`}
      >
        <div>
          <h6 className="mb-0 text-dark font-weight-bold text-sm">
            契約関連書類の添付
            {task!.has_document || task!.none_documents ? (
              <></>
            ) : (
              <i className="fas fa-exclamation-circle ms-1 text-danger"></i>
            )}
          </h6>
          <div className="text-weak text-xs mt-1">承認依頼対象の契約関連書類を添付してください。</div>
        </div>
        {!task!.has_document && (
          <div className="form-check mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="noneDocumentsCheck"
              checked={task!.none_documents}
              onChange={onNoneDocumentsChange}
              disabled={disabled}
            />
            <label className="custom-control-label" htmlFor="noneDocumentsCheck">
              関連書類なし
            </label>
          </div>
        )}
      </div>
      <hr className="horizontal dark mt-4 mb-0" />
    </li>
  );
};

const BillingScheduleCheckElement = ({ disabled }: { disabled: boolean }) => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember, me } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { salesOrderContractTask: task, setSalesOrderContractTask } = useCurrentSalesOrderContractTask();
  const { mutate, data, isPending, error } = useUpdateSalesOrderContractBillingScheduleChecked();
  const {
    data: quotationBoardAmount,
    isLoading: isQuotationBoardAmountPending,
    error: isQuotationBoardAmountError,
    refetch: refetchQuotationBoardAmount,
  } = useQuotationBoardAmount({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id!),
    enabled: false,
  });

  useEffect(
    () => setShowLoading(isPending || isQuotationBoardAmountPending),
    [isPending, isQuotationBoardAmountPending]
  );
  useEffect(() => toastApiError(error || isQuotationBoardAmountError), [error, isQuotationBoardAmountError]);

  const onBillingScheduleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id!),
      id: task!.id,
      billing_schedule_checked: e.target.checked,
    });
  };

  const onQuotationBoardAmountClick = () => {
    refetchQuotationBoardAmount();
  };

  useEffect(() => {
    if (data) setSalesOrderContractTask((_) => data);
  }, [data]);

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 mb-3">
      <div
        className={`checklist-item ps-2 ms-3 ${
          task!.billing_schedule_checked ? 'checklist-item-success' : 'checklist-item-danger'
        }`}
      >
        <div>
          <h6 className="mb-0 text-dark font-weight-bold text-sm">
            プロジェクト計画金額や期間の最新化
            {task!.billing_schedule_checked ? <></> : <i className="fas fa-exclamation-circle ms-1 text-danger"></i>}
          </h6>
          <div className="text-weak text-xs mt-1">
            {!disabled && (
              <>
                プロジェクトの計画金額や期間が変更になっている場合、
                <Link
                  to={`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${sales_order_id}/edit`}
                  state={{ prevPath: location.pathname }}
                  style={{ textDecoration: 'underline' }}
                >
                  こちら
                </Link>
                から変更してください。
              </>
            )}
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12 col-md-5">
            <div className="form-group">
              <label className="mx-0">プロジェクト計画金額（税抜）</label>
              <div className="form-control-plaintext">
                ¥{task!.sales_order.accounting_total_amount.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="col-12 col-md-7">
            {task!.sales_order.sales_order_board_project != null && (
              <div className="form-group">
                <label className="mx-0">見積合計金額</label>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="form-control-plaintext flex-1">
                    ¥{quotationBoardAmount ? quotationBoardAmount.amount.toLocaleString() : ' -'}
                  </div>
                  <button
                    className="btn btn-icon bg-gradient-dark ms-1 btn-sm px-3"
                    type="button"
                    disabled={disabled}
                    onClick={onQuotationBoardAmountClick}
                  >
                    boardから取得
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            value=""
            id="billingScheduleCheck"
            checked={task!.billing_schedule_checked}
            onChange={onBillingScheduleChange}
            disabled={disabled}
          />
          <label className="custom-control-label" htmlFor="billingScheduleCheck">
            プロジェクトの期間や金額が正しいことを確認
          </label>
        </div>
      </div>
    </li>
  );
};

const LegalCheckElement = ({ disabled }: { disabled: boolean }) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, meTeamMember, me } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { salesOrderContractTask: task, setSalesOrderContractTask } = useCurrentSalesOrderContractTask();
  const { mutate, data, isPending, error } = useUpdateSalesOrderContractLegalChecked();

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onLegalCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id!),
      id: task!.id,
      legal_checked: e.target.checked,
    });
  };

  useEffect(() => {
    if (data) setSalesOrderContractTask((_) => data);
  }, [data]);

  return (
    <li className="list-group-item border-0 flex-column align-items-start ps-0 py-0 mb-3">
      <div
        className={`checklist-item ps-2 ms-3 ${
          task!.legal_checked ? 'checklist-item-success' : 'checklist-item-danger'
        }`}
      >
        <div>
          <h6 className="mb-0 text-dark font-weight-bold text-sm">
            法務確認
            {task!.legal_checked ? <></> : <i className="fas fa-exclamation-circle ms-1 text-danger"></i>}
          </h6>
          <div className="text-weak text-xs mt-1">
            チーム内のルールに従い対応が必要な場合は、法務確認を行なってください。
          </div>
        </div>
        <div className="form-check mt-3">
          <input
            className="form-check-input"
            type="checkbox"
            value=""
            id="legalCheck"
            checked={task!.legal_checked}
            onChange={onLegalCheckChange}
            disabled={disabled}
          />
          <label className="custom-control-label" htmlFor="legalCheck">
            法務確認不要、または法務確認の上問題ないことを確認
          </label>
        </div>
      </div>
    </li>
  );
};
